import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { COMPANY, HERO_STATS } from "../../data/whitespaces";
import { MaskLineLoad } from "../../lib/motionPrimitives";
import ThreeHero from "../ThreeHero";

export const Hero = ({ onScrollTo }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  const wordmarkY = useTransform(scrollYProgress, [0, 1], [0, 180]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden px-6 pb-14 pt-28 md:px-12 md:pt-24"
    >
      {/* 3D morphing point-cloud */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <ThreeHero />
      </div>
      <div className="pointer-events-none absolute inset-0 z-0 spotlight" />
      <div className="pointer-events-none absolute inset-0 z-0 spotlight-warm" />

      {/* giant brand watermark — parallax, part of the scroll experience */}
      <motion.span
        style={{ y: wordmarkY, opacity: contentOpacity }}
        className="pointer-events-none absolute inset-x-0 bottom-[-4%] z-0 select-none text-center font-display text-[26vw] font-bold leading-none tracking-tighter text-white/[0.03] md:text-[22vw]"
        aria-hidden
      >
        WHITESPACES
      </motion.span>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto w-full max-w-[1600px]"
      >
        <div className="max-w-4xl">
          {/* headline — masked line-by-line load, generous leading so descenders never clip */}
          <h1
            className="font-display text-[12vw] font-semibold leading-[1.03] tracking-[-0.02em] text-white md:text-[7vw] lg:text-[5.4vw]"
            style={{ textShadow: "0 2px 40px rgba(0,0,0,0.55)" }}
          >
            {COMPANY.heroLines.map((line, i) => (
              <MaskLineLoad
                key={i}
                delay={0.5 + i * 0.14}
                className={`pb-[0.12em] ${i % 2 === 1 ? "text-teal" : "text-white"}`}
              >
                {line}
              </MaskLineLoad>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            data-testid="hero-subheading"
            className="mt-6 font-body text-lg text-white/70 md:text-xl"
          >
            <span className="font-medium text-white">Whitespaces</span>: Scaling brands through
            untapped segments.
          </motion.p>

          {/* motto + cta */}
          <div className="mt-10 grid gap-10 md:grid-cols-[1.4fr_1fr] md:items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 1 }}
              className="max-w-xl font-body text-base leading-relaxed text-white/85 md:text-lg"
            >
              {COMPANY.motto}
            </motion.p>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.8 }}
              data-testid="hero-explore-btn"
              onClick={() => onScrollTo("personas")}
              className="group flex items-center gap-4 justify-self-start md:justify-self-end"
            >
              <span className="font-body text-[12px] uppercase tracking-[0.25em] text-white/70 transition-colors group-hover:text-white">
                Explore the deck
              </span>
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 transition-all duration-300 group-hover:border-teal group-hover:bg-teal">
                <ArrowDown className="text-white transition-colors group-hover:text-black" size={18} />
              </span>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* bottom stat strip */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7, duration: 0.9 }}
        style={{ opacity: contentOpacity }}
        className="relative z-10 mx-auto mt-14 grid w-full max-w-[1600px] grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md md:grid-cols-4"
      >
        {HERO_STATS.map((s) => (
          <div key={s.label} className="px-6 py-6 md:px-8 md:py-7">
            <div className="font-display text-3xl font-medium text-white md:text-4xl">{s.value}</div>
            <div className="mt-1 font-body text-[11px] uppercase tracking-[0.15em] text-white/45">
              {s.label}
            </div>
          </div>
        ))}
      </motion.div>
    </section>
  );
};

export default Hero;
